import * as d3 from 'd3';
import Tooltip from './Tooltip';

export default class BarChart {
  constructor({
    target, data, vars, plot,
  }) {
    this.target = d3.select(target);
    this.data = data;
    this.catVar = vars.cat;
    this.numVar = vars.num;
    this.margin = plot.margin;
    this.colorPalette = plot.colorPalette;
    this.plotLayout = plot.mode || 'landscape';
    this.plotTooltip = plot.tooltip;
    this.plotDomain = plot.domain;
    this.plotHasBarsLabels = plot.barsLabel;
    this.barsLabelFormatter = plot.labelFormatter;
    this.yAxisTitle = plot.yAxisTitle;
    this.plotOuterWidth = null;
    this.plotOuterHeight = null;
    this.plotInnerWidth = null;
    this.plotInnerHeight = null;
    this.plotArea = null;
    this.scales = {
      x: null,
      y: null,
      colors: null,
    };
    this.axis = {
      x: null,
      y: null,
    };
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
  }

  setData(data) {
    this.data = data;
  }

  updateDimensions() {
    const width = this.target.node().parentNode.offsetWidth;
    const height = width;

    this.plotOuterWidth = width;
    this.plotOuterHeight = height;
    this.plotInnerWidth = width - this.margin.left - this.margin.right;
    this.plotInnerHeight = height - this.margin.top - this.margin.bottom;

    this.target.attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${this.plotOuterWidth} ${this.plotOuterHeight}`);
  }

  draw() {
    this.updateDimensions();

    if (this.plotTooltip) {
      this.tooltip = new Tooltip(this.target.node().parentNode);
    }


    this.plotArea = this.target.append('g')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.rectArea = this.plotArea.append('g').attr('class', 'rects');

    this.scales.x = d3.scaleLinear()
      .range([0, this.plotInnerWidth]);

    this.scales.y = d3.scaleBand()
      .range([0, this.plotInnerHeight])
      .padding(0.5);

    if (Array.isArray(this.colorPalette)) {
      if (this.colorPalette) {
        this.scales.colors = d3.scaleOrdinal()
          .range(this.colorPalette);
      } else {
        this.scales.colors = d3.scaleOrdinal(d3.schemeCategory10);
      }
    } else {
      this.scales.colors = () => this.colorPalette;
    }

    if (this.plotArea.select('g.labels').empty() && this.plotHasBarsLabels) {
      this.labelsWrapper = this.plotArea.append('g').classed('labels', true);
    }


    this.axis.y = this.plotArea.append('g')
      .classed('y-axis', true);

    if (this.yAxisTitle) {
      this.axis.y.append('text')
        .attr('fill', 'currentColor')
        .attr('text-anchor', 'middle')
        .text(this.yAxisTitle);
    }

    this.update();
  }

  update() {
    if (this.plotDomain) {
      this.scales.x.domain(this.plotDomain);
    } else {
      this.scales.x.domain([0, d3.max(this.data, (d) => d[this.numVar])]);
    }

    this.scales.y.domain(this.data.map((d) => d[this.catVar]));
    this.axis.y.transition().duration(750).call(d3.axisLeft(this.scales.y).tickSize(0));
    this.axis.y.call((g) => g.select('.domain').remove());
    // this.axis.y.selectAll('.tick text').call(this.wrap, this.margin.left);

    const rects = this.rectArea.selectAll('rect').data(this.data);
    const self = this;

    rects.enter()
      .append('rect')
      .attr('width', 0)
      .merge(rects)
      .transition()
      .duration(1000)
      .attr('x', this.scales.x(0))
      .attr('y', (d) => this.scales.y(d[this.catVar]))
      .attr('width', (d) => this.scales.x(d[this.numVar]))
      .attr('height', this.scales.y.bandwidth())
      .attr('fill', (d, i) => this.scales.colors(i));

    if (this.plotTooltip) {
      this.rectArea.selectAll('rect')
        .on('mouseover', function getDatum() {
          const datum = d3.select(this).datum();
          self.handleMouseOver(datum);
        })
        .on('mousemove', this.handleMouseMove)
        .on('mouseleave', this.handleMouseLeave);
    }

    rects.exit().remove();

    if (!this.plotHasBarsLabels) {
      return;
    }

    const labels = this.labelsWrapper.selectAll('text').data(this.data);

    labels.enter()
      .append('text')
      .style('opacity', '0')
      .merge(labels)
      .transition()
      .duration(1000)
      .attr('x', 10)
      .attr('y', (d) => this.scales.y(d[this.catVar]))
      .attr('transform', `translate(0, ${this.scales.y.bandwidth()})`)
      .attr('font-size', '0.75rem')
      .attr('fill', '#2D3748')
      .style('pointer-events', 'none')
      .style('opacity', '1')
      .text((d) => this.barsLabelFormatter(d[this.numVar]));

    labels.exit().remove();
  }

  delete() {
    this.plotArea.remove();
  }

  updateRanges() {
    this.scales.x.range([0, this.plotInnerWidth]);
    this.scales.y.range([0, this.plotInnerHeight]);
  }

  resize() {
    this.updateDimensions();
    this.updateRanges();
    this.update();
  }

  // eslint-disable-next-line no-unused-vars, class-methods-use-this
  handleMouseOver(datum) {
    // Implemented by decorators
  }

  handleMouseMove() {
    const x = d3.event.layerX;
    const y = d3.event.layerY;
    this.tooltip.updatePosition(x, y);
  }

  handleMouseLeave() {
    this.tooltip.html('').hide();
  }

  // eslint-disable-next-line class-methods-use-this
  wrap(text, width) {
    text.each(function calculateWrapper() {
      let line = [];
      let lineNumber = 1;
      const selection = d3.select(this);
      const words = selection.text().split(/\s+/).reverse();
      const x = selection.attr('x');
      const dy = parseFloat(selection.attr('dy'));
      let tspan = selection.text(null)
        .append('tspan')
        .attr('x', x)
        .attr('dy', `${dy}em`);
      let word = words.pop();
      while (word) {
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = selection.append('tspan')
            .attr('x', x)
            // eslint-disable-next-line no-plusplus
            .attr('dy', `${lineNumber - 0.3 + dy}em`)
            .text(word);
          lineNumber += 1;
        }
        word = words.pop();
      }
    });
  }
}
